import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { IMapLayoutFinish } from '@interfaces/map';

import {
  legacyRoomKey,
  mintRoomKey,
  sameRoomPlacements,
  type MapEditorRoom,
} from './mapEditorRooms';
import { removeFinish, sameFinishes, upsertFinish } from './mapEditorFinishes';
import {
  EMPTY_FINISH_SET,
  type MapLayoutFinishSet,
  type MapLayoutRefusal,
  type MapLayoutStandingRoom,
} from './mapEditorUnreal';

export interface MapEditorContextValue {
  // The draft. Every edit below is local to this author — nothing reaches Unreal, and
  // therefore nothing reaches other participants, until publishLayout is called.
  // Duplicates are allowed and meaningful (two galleries in a row), so this is a list.
  //
  // Each room carries a key identifying it as a placement rather than as an element, which is what
  // lets the same module appear twice and still address its own spots. Keys are minted once, when
  // the author adds the room, and travel with it through every edit below — see mapEditorRooms.
  rooms: MapEditorRoom[];
  // Adds a segment at the end of the walk and returns the placement key it was minted with, so the
  // caller can select the row it has just created. Empty for an empty element id, which adds nothing.
  appendRoom: (elementId: string) => string;
  removeRoomAt: (index: number) => void;
  moveRoom: (index: number, direction: -1 | 1) => void;
  // Swaps the module standing at a placement without disturbing the placement. The key survives, so
  // the content assigned to that segment's walls survives with it — see replaceRoomAt.
  replaceRoomAt: (index: number, elementId: string) => void;
  // Replaces the whole chain, minting a placement for each. What a starter layout drops in.
  setChain: (elementIds: string[]) => void;
  clearChain: () => void;

  // The lobby this layout opens in, as a catalog element id. Empty means it opens on the entrance
  // the host map bakes in, which is what every layout said before an author could choose.
  //
  // Held beside the rooms rather than as `rooms[0]`, because it is not a link in the chain: it is
  // never moved, reordered or removed, the picker never offers it, and every index the room list
  // counts — the refusal markers, the move buttons, the position labels — would shift by one if it
  // lived there. It is prepended at publish time, the way the cap is appended.
  startElement: string;
  // Chooses the lobby. Empty goes back to the map's baked entrance, which is a real choice rather
  // than a reset: it is the only way to undo having picked one.
  setStartElement: (elementId: string) => void;

  // Throws the draft away and goes back to the layout that is standing. The recovery path out of
  // Clear, which otherwise leaves an author with an empty list, a world Unreal has hidden from them,
  // and no record of what was there.
  revertDraft: () => void;
  // There is something standing to go back to, and the draft is not already it.
  canRevert: boolean;

  // The draft as it stood at the last publish. Null means nothing has been published
  // from this session yet.
  publishedRooms: MapEditorRoom[] | null;
  // Records a publish. Called by the inspector alongside the SPAWN_LAYOUT send.
  markPublished: (
    rooms: MapEditorRoom[],
    finishes?: MapLayoutFinishSet,
    startElement?: string,
  ) => void;
  // Draft differs from what was last published — i.e. this author is looking at
  // something the other participants are not.
  isDirty: boolean;

  // Rooms Unreal reports as actually standing (CURRENT_GEOMETRY_LIST). This is what
  // everyone currently sees, which is shorter than the published draft when Unreal
  // refused a room for overlapping one already placed.
  //
  // Carries each room's key and, once its level has been read, the addressable id of every spot in
  // it. That is the authoritative account of which spot ids exist in the world: a republish whose
  // rooms are unchanged is answered by doing nothing, so keys reach the world only when the layout
  // itself does.
  builtRooms: MapLayoutStandingRoom[] | null;
  // `standingStart` is the lobby of the same report, already taken out of the room list. Passed
  // alongside rather than through a setter of its own so one report is adopted as one event: both
  // halves seed the draft on the same one-shot rule, and two setters racing over it would let the
  // rooms of a report seed a draft while its lobby did not.
  setBuiltRooms: (
    rooms: MapLayoutStandingRoom[] | null,
    standingStart?: string,
  ) => void;

  // Rooms the authority turned down in the same report, keyed by their position in the chain that
  // was published. This is the only thing that distinguishes "Unreal built fewer rooms than I asked
  // for" from "I asked for fewer rooms" — without it a refused room simply vanishes with no reason.
  refusals: MapLayoutRefusal[] | null;
  setRefusals: (refusals: MapLayoutRefusal[] | null) => void;

  // The same, for the draft Unreal is previewing locally. Separate from `refusals` because the two are
  // keyed against different chains: `refusals` describes the chain that was last published, and goes
  // stale the moment the author edits, while this one is re-sent on every edit and always describes the
  // chain in the list right now. It is the only account of a draft that stopped building partway down —
  // a preview is never reported as a geometry list, since that message describes the shared world.
  previewRefusals: MapLayoutRefusal[] | null;
  setPreviewRefusals: (refusals: MapLayoutRefusal[] | null) => void;

  // Adopts the layout saved on the branding record as the starting draft. Unlike the
  // CURRENT_GEOMETRY_LIST seed this survives a refresh and reaches a second author on
  // another machine, because it is the author's saved intent rather than a report of what
  // one Unreal instance happens to have standing right now.
  //
  // `scopeId` is the branding the rooms came out of. It is what makes this safe to call again:
  // a new scope replaces the draft outright, the same scope keeps the one-shot rule.
  //
  // `treatAsPublished` records the hydrated rooms as the published baseline. Only for a record
  // this hub had to rewrite on read — see the persistence bridge.
  hydrateFromPersisted: (
    rooms: MapEditorRoom[],
    scopeId: string,
    treatAsPublished?: boolean,
    finishes?: MapLayoutFinishSet,
    savedStart?: string,
  ) => void;

  // True once the author has changed the chain themselves. A restored or seeded chain describes a
  // layout that already exists elsewhere, so it is not something to build locally — previewing it
  // duplicated whatever the server was already showing.
  hasAuthorEdits: boolean;

  // The draft's finishes: what every room takes, and what individual placements override.
  //
  // Part of the layout rather than a thing beside it — they publish with it, persist with it, and
  // make it dirty. Held here for the same reason the rooms are: the provider outlives the overlay,
  // so closing the panel does not throw away an unpublished surface change.
  finishes: MapLayoutFinishSet;
  // Puts a finish on a semantic slot. `roomKey` null means every room — the layout's default, which
  // is the level authors actually decide a surface at; a key overrides that one placement.
  setSlotFinish: (roomKey: string | null, finish: IMapLayoutFinish) => void;
  // Takes the finish off a slot, which is not the same as finishing it with the kit's own material:
  // an unfinished slot is expressed by having no entry, and UE leaves the authored material alone.
  clearSlotFinish: (roomKey: string | null, slot: string) => void;
}

const MapEditorContext = createContext<MapEditorContextValue | null>(null);

/**
 * Reads a geometry report as a draft: the author's own rooms, each with the key it is standing under.
 *
 * The reported key is adopted verbatim rather than re-minted, even though UE may have stamped it
 * itself from the room's index. It is the key the spots in that room are addressed by right now, and
 * a fresh one would re-point every one of them for nothing.
 *
 * A room reported without one gets the index fallback rather than an empty key, on exactly the rule
 * readPersistedRooms applies to a v1 record — and for a sharper reason than tidiness. Every control
 * that acts on a segment is addressed by placement key now: the tree selects by it, and selecting is
 * what opens the panel that holds Change, the reorder pair, Remove and the surfaces. An empty key
 * cannot be selected (see selectMapSegment), so a row carrying one would render and then refuse every
 * edit, with nothing on screen saying why. `legacyRoomKey` reproduces the string UE's own fallback
 * stamps for that position, so adopting it also keeps the republish on entry a no-op.
 *
 * @param rooms - Standing rooms from CURRENT_GEOMETRY_LIST, caps already dropped.
 * @returns The same rooms as draft entries, each with a non-empty key.
 */
const asDraftRooms = (rooms: MapLayoutStandingRoom[]): MapEditorRoom[] =>
  rooms.map((room, index) => ({
    id: room.id,
    key: room.roomKey || legacyRoomKey(index),
  }));

/**
 * Whether two finish sets describe the same surfaces.
 *
 * @param a - One set.
 * @param b - The other.
 * @returns True when the defaults and every placement's overrides match.
 */
const sameFinishSets = (
  a: MapLayoutFinishSet,
  b: MapLayoutFinishSet,
): boolean => {
  if (!sameFinishes(a.defaults, b.defaults)) {
    return false;
  }

  const keys = new Set([...Object.keys(a.rooms), ...Object.keys(b.rooms)]);

  return [...keys].every((key) =>
    sameFinishes(a.rooms[key] ?? [], b.rooms[key] ?? []),
  );
};

/**
 * Holds the map-editor draft: the rooms this author has assembled and how they relate to what is
 * standing in the world.
 *
 * Mounted above the editor overlay, so the draft and the published baseline survive the overlay
 * being closed and a branding being switched.
 *
 * @param props.children - Anything reading the draft through useMapEditor.
 */
export const MapEditorProvider = ({ children }: { children: ReactNode }) => {
  const [rooms, setRooms] = useState<MapEditorRoom[]>([]);
  const [publishedRooms, setPublishedRooms] = useState<MapEditorRoom[] | null>(
    null,
  );
  const [finishes, setFinishes] =
    useState<MapLayoutFinishSet>(EMPTY_FINISH_SET);
  const [publishedFinishes, setPublishedFinishes] =
    useState<MapLayoutFinishSet | null>(null);
  const [builtRooms, setBuiltRoomsState] = useState<
    MapLayoutStandingRoom[] | null
  >(null);
  const [refusals, setRefusals] = useState<MapLayoutRefusal[] | null>(null);
  const [startElement, setStartElementState] = useState('');
  // The lobby as it stood at the last publish. Null means no baseline yet, exactly as it does for
  // the rooms and the finishes; the empty string is a real value here — "opens on the map's baked
  // entrance" — so it cannot double as "nothing recorded".
  const [publishedStartElement, setPublishedStartElement] = useState<
    string | null
  >(null);
  const [previewRefusals, setPreviewRefusals] = useState<
    MapLayoutRefusal[] | null
  >(null);

  // Unreal reports the standing layout after any spawn or clear, including ones this
  // author did not make. The first such report is adopted as the starting draft so an
  // author opening the editor begins from what is actually built rather than an empty
  // list — but only once, and only while they have not started editing, so a report
  // arriving mid-edit can never overwrite work in progress.
  const hasSeededRef = useRef(false);
  const hasEditedRef = useRef(false);

  // Whether this author has published from this session. Until they have, CURRENT_GEOMETRY_LIST is
  // the only account of what is standing, so every report is allowed to set the baseline. Once they
  // publish, their own intent takes over — see setBuiltRooms and markPublished.
  const hasPublishedRef = useRef(false);

  // Mirrors hasEditedRef as state, because two things outside this provider have to react to it.
  //
  // A restored or seeded chain is not a draft — it is a description of a layout that already
  // exists elsewhere. Previewing it put the author's local copy on top of the rooms the server
  // was already showing them, which is the doubled geometry seen on entry. Only edits the author
  // actually made are worth building locally.
  const [hasAuthorEdits, setHasAuthorEdits] = useState(false);

  const setBuiltRooms = useCallback(
    (standingRooms: MapLayoutStandingRoom[] | null, standingStart = '') => {
      setBuiltRoomsState(standingRooms);

      if (!standingRooms) {
        return;
      }

      // Recording what is standing is a different job from seeding the draft, and it must not be
      // gated on it. hydrateFromPersisted wins the seed almost every time — the branding fetch beats
      // a round trip through Unreal — and while both jobs shared hasSeededRef, the report could never
      // do the one thing only it is allowed to do. Every client that had not published in this
      // session therefore read a standing layout as an unsaved draft, and said so in the inspector.
      //
      // Not conditioned on publishedRooms being unset either: a first report of an empty world
      // (a server that restarted before anyone republished) is a legitimate baseline, and the report
      // that follows the entry republish has to be able to replace it.
      if (!hasPublishedRef.current) {
        setPublishedRooms(asDraftRooms(standingRooms));
        // Recorded even when empty, and that is the point: a report saying the world opens on the
        // map's baked entrance is an answer, so a baseline that left it out would read every such
        // world as one whose author had cleared the lobby.
        setPublishedStartElement(standingStart);
      }

      if (hasSeededRef.current || hasEditedRef.current) {
        return;
      }
      hasSeededRef.current = true;
      setRooms(asDraftRooms(standingRooms));
      setStartElementState(standingStart);
    },
    [],
  );

  // Which branding the draft currently describes. Null until the first hydrate.
  const scopeRef = useRef<string | null>(null);

  // Same one-shot discipline as the geometry seed, and it shares hasSeededRef with it so the
  // two can never fight: whichever arrives first defines the starting draft, and the other is
  // ignored rather than overwriting it. Editing locks both out for the rest of the session.
  //
  // The one-shot rule is per branding, not per session. This provider is mounted above the editor
  // overlay so it outlives a configuration switch, and the switch republishes the world from the
  // newly activated record — so a draft still describing the previous configuration is not work in
  // progress, it is a stale list pointed at rooms that are no longer standing. Left in place it
  // read as unsaved changes, and Save wrote the old configuration's rooms into the new
  // configuration's record.
  //
  // Everything scoped to the old branding goes with it: the published baseline, the geometry report
  // and its refusals all describe a world that has just been replaced. Only the very first hydrate
  // defers to hasEditedRef — an author who added a room before the branding fetch resolved keeps it,
  // which is the case that rule was written for.
  //
  // Deliberately does NOT mark the layout published by default. The branding record holds the
  // author's last saved *intent*, which is not evidence that the world was ever built from it — a
  // save can be written while the spawn fails, and a server can be cleared or restarted afterwards.
  // Treating intent as proof disabled Save on a restored chain, leaving an author looking at rooms
  // they could not republish because the button was dead. Only CURRENT_GEOMETRY_LIST, which reports
  // what Unreal actually has standing, is normally allowed to mark a layout published.
  //
  // `treatAsPublished` is the one exception, and it is not about intent at all: it is for a record
  // this hub rewrote on read. The dropped row was never the author's edit, so surfacing it as their
  // unsaved change would be this hub reporting its own bookkeeping as their work — and where the
  // rewrite cannot be reconciled against the geometry report at all, it would say so on every entry,
  // forever. See dropTrailingCap.
  const hydrateFromPersisted = useCallback(
    (
      savedRooms: MapEditorRoom[],
      scopeId: string,
      treatAsPublished = false,
      savedFinishes: MapLayoutFinishSet = EMPTY_FINISH_SET,
      savedStart = '',
    ) => {
      const isNewScope = !!scopeRef.current && scopeRef.current !== scopeId;

      // Recorded before the early return, not after it: this call may well decline to reseed — the
      // geometry report usually wins the first seed — and the draft still has to know which
      // configuration it belongs to, or the *next* one is not recognisable as a different one and
      // the switch goes unnoticed exactly as it did before.
      scopeRef.current = scopeId;

      if (!isNewScope && (hasSeededRef.current || hasEditedRef.current)) {
        return;
      }

      hasSeededRef.current = true;
      setRooms(savedRooms);
      setFinishes(savedFinishes);
      setStartElementState(savedStart);

      if (isNewScope) {
        hasEditedRef.current = false;
        setHasAuthorEdits(false);
        setBuiltRoomsState(null);
        setRefusals(null);
        setPreviewRefusals(null);
      }

      hasPublishedRef.current = treatAsPublished;
      setPublishedRooms(treatAsPublished ? [...savedRooms] : null);
      setPublishedFinishes(treatAsPublished ? savedFinishes : null);
      setPublishedStartElement(treatAsPublished ? savedStart : null);
    },
    [],
  );

  const editRooms = useCallback(
    (update: (prev: MapEditorRoom[]) => MapEditorRoom[]) => {
      hasEditedRef.current = true;
      setHasAuthorEdits(true);
      setRooms(update);
    },
    [],
  );

  // A key is minted here and nowhere else in the edit path: adding a room is the only edit that
  // creates a placement. Adding the same element twice therefore mints twice, which is the case
  // per-placement content exists for — two galleries in a row addressing their own walls.
  const appendRoom = useCallback(
    (elementId: string) => {
      if (!elementId) {
        return '';
      }
      // Minted out here rather than inside the updater, and returned. The caller has to be able to
      // select the row it just added, and a key minted inside the updater is not knowable from
      // outside it — which left the selection to be guessed from the list's shape.
      const key = mintRoomKey();
      editRooms((prev) => [...prev, { id: elementId, key }]);

      return key;
    },
    [editRooms],
  );

  const removeRoomAt = useCallback(
    (index: number) => {
      editRooms((prev) => {
        // The removed placement's overrides go with it. Left behind they would be orphans keyed to a
        // room nobody can see, and they would come back to life the day a new placement happened to
        // mint the same key — which is unlikely rather than impossible, and silent when it happens.
        const removed = prev[index];
        if (removed?.key) {
          setFinishes((current) => {
            if (!current.rooms[removed.key]) {
              return current;
            }
            const { [removed.key]: _dropped, ...kept } = current.rooms;

            return { ...current, rooms: kept };
          });
        }

        return prev.filter((_, i) => i !== index);
      });
    },
    [editRooms],
  );

  // Swaps which module stands at a placement, keeping the placement itself. What **Change** on a
  // segment does.
  //
  // The key is deliberately kept rather than re-minted, and that is the whole difference between
  // this and remove-then-add. The placement is the author's — its position in the walk, the content
  // assigned to its walls, the finishes put on its surfaces — and only the geometry filling it is
  // being replaced. Re-minting would silently unassign every picture in the room.
  //
  // The finishes are kept for the same reason, and the surfaces the new module does not offer are
  // *not* pruned here: `finishableSlotsFor` is what decides which of them the panel offers and the
  // payload builder sends, so an override for a surface the new module lacks is inert rather than
  // wrong — and it comes back intact if the author changes their mind, which they are mid-way
  // through doing.
  const replaceRoomAt = useCallback(
    (index: number, elementId: string) => {
      if (!elementId) {
        return;
      }
      editRooms((prev) => {
        if (index < 0 || index >= prev.length || prev[index].id === elementId) {
          return prev;
        }
        const next = [...prev];
        next[index] = { id: elementId, key: prev[index].key };

        return next;
      });
    },
    [editRooms],
  );

  // Replaces the whole chain in one edit. What a starter layout does.
  //
  // Every placement is new, so every key is minted — the opposite rule to replaceRoomAt, and right
  // for the opposite reason: nothing here is a placement the author had, so there is nothing whose
  // content has to survive. The per-room overrides go with the chain they were addressed to, on the
  // same rule clearChain applies, and the layout-wide defaults stay.
  const setChain = useCallback(
    (elementIds: string[]) => {
      editRooms(() =>
        elementIds
          .filter((id) => !!id)
          .map((id) => ({ id, key: mintRoomKey() })),
      );
      setFinishes((current) =>
        Object.keys(current.rooms).length ? { ...current, rooms: {} } : current,
      );
    },
    [editRooms],
  );

  // Moves the room, key and all. Nothing is re-minted and nothing is re-derived from the new
  // position: a key that moved with its row is the entire point of having one, since the content
  // assigned to that placement has to move with it rather than staying at the index.
  const moveRoom = useCallback(
    (index: number, direction: -1 | 1) => {
      editRooms((prev) => {
        const target = index + direction;
        if (
          index < 0 ||
          index >= prev.length ||
          target < 0 ||
          target >= prev.length
        ) {
          return prev;
        }
        const next = [...prev];
        [next[index], next[target]] = [next[target], next[index]];
        return next;
      });
    },
    [editRooms],
  );

  // Chooses the lobby, and counts as an edit like any other — it changes what everyone will see
  // when they arrive, which is the most visible change in this panel rather than the least.
  //
  // Deliberately not routed through editRooms: the lobby is not one of the rooms, and passing it
  // through a room updater would be the same conflation this whole feature is built to avoid.
  const setStartElement = useCallback((elementId: string) => {
    hasEditedRef.current = true;
    setHasAuthorEdits(true);
    setStartElementState(elementId);
  }, []);

  // Empties the draft only. The standing layout is untouched until the author saves —
  // at which point an empty draft publishes as a clear, since SPAWN_LAYOUT carries
  // clearExisting.
  //
  // The lobby is deliberately left standing. Clear empties the chain the author assembled, and the
  // lobby is not part of it: an empty layout is the entrance on its own, so clearing back to it is
  // the floor rather than a state to clear past. Un-choosing one is its own edit.
  const clearChain = useCallback(() => {
    editRooms(() => []);
    // The per-room overrides go; the defaults stay. An override is addressed to a placement that no
    // longer exists, while a default is a decision about a surface — "walls are concrete here" — and
    // it is the answer again the moment the author starts rebuilding.
    setFinishes((current) =>
      Object.keys(current.rooms).length ? { ...current, rooms: {} } : current,
    );
  }, [editRooms]);

  // Both finish edits go through here, so marking the draft edited is not something either can
  // forget. A finish change is an edit like any other: it has to make the draft dirty, or Save is
  // dead for an author who changed nothing but a surface.
  const editFinishes = useCallback(
    (update: (prev: MapLayoutFinishSet) => MapLayoutFinishSet) => {
      hasEditedRef.current = true;
      setHasAuthorEdits(true);
      setFinishes(update);
    },
    [],
  );

  const setSlotFinish = useCallback(
    (roomKey: string | null, finish: IMapLayoutFinish) => {
      editFinishes((prev) => {
        if (!roomKey) {
          return { ...prev, defaults: upsertFinish(prev.defaults, finish) };
        }

        return {
          ...prev,
          rooms: {
            ...prev.rooms,
            [roomKey]: upsertFinish(prev.rooms[roomKey] ?? [], finish),
          },
        };
      });
    },
    [editFinishes],
  );

  const clearSlotFinish = useCallback(
    (roomKey: string | null, slot: string) => {
      editFinishes((prev) => {
        if (!roomKey) {
          return { ...prev, defaults: removeFinish(prev.defaults, slot) };
        }

        const remaining = removeFinish(prev.rooms[roomKey] ?? [], slot);
        // An empty override list is dropped rather than kept as an empty array: on the wire the two
        // are different statements — no overrides at all, against a room deliberately finished with
        // nothing — and only the first one is what clearing the last override means.
        if (!remaining.length) {
          const { [roomKey]: _cleared, ...kept } = prev.rooms;

          return { ...prev, rooms: kept };
        }

        return { ...prev, rooms: { ...prev.rooms, [roomKey]: remaining } };
      });
    },
    [editFinishes],
  );

  // What reverting goes back to: what Unreal reports standing, falling back to the last published
  // intent only when no report has arrived yet. The two differ whenever a room was refused, and
  // going back to intent would restore rows that are not in the world — the opposite of matching it.
  const revertTarget = useMemo(
    () => (builtRooms ? asDraftRooms(builtRooms) : publishedRooms),
    [builtRooms, publishedRooms],
  );

  // Discards the draft and puts the standing layout back in the list.
  //
  // Needed because Clear is otherwise a one-way door: it empties the list, and Unreal hides the
  // published rooms for as long as any draft is up — so the author is left looking at an empty world
  // with no record of what they had and nothing to restore it from.
  //
  // Deliberately not routed through editRooms: clearing hasAuthorEdits is the point. It lets the
  // preview bridge take the local draft down and hand the real rooms back, instead of building a
  // second copy of geometry the server is already showing.
  const revertDraft = useCallback(() => {
    if (!revertTarget) {
      return;
    }
    hasEditedRef.current = false;
    setHasAuthorEdits(false);
    setRooms(revertTarget);
    // Finishes go back to the last published set, and to nothing when there is none. Unlike the
    // rooms there is no geometry report to fall back on for them before a first publish, so
    // "nothing" is the honest answer rather than a guess at what is standing.
    setFinishes(publishedFinishes ?? EMPTY_FINISH_SET);
    // The lobby does have a report to fall back on — setBuiltRooms records the standing one as the
    // baseline until this author publishes — so the baseline is the answer here too, and its empty
    // string is the right one for a world that opens on the map's baked entrance.
    setStartElementState(publishedStartElement ?? '');
  }, [revertTarget, publishedFinishes, publishedStartElement]);

  // Records what this author asked for, which from here on outranks what Unreal reports standing.
  // The two differ whenever a room was refused, and letting the report win there would re-raise
  // "unsaved" the instant a publish came back short — telling the author their change had not been
  // applied when it had been, as far as it could be. The refused rows say so individually instead.
  const markPublished = useCallback(
    (
      publishedRoomList: MapEditorRoom[],
      publishedFinishSet: MapLayoutFinishSet = EMPTY_FINISH_SET,
      publishedStart = '',
    ) => {
      hasPublishedRef.current = true;
      setPublishedRooms([...publishedRoomList]);
      setPublishedFinishes(publishedFinishSet);
      setPublishedStartElement(publishedStart);
    },
    [],
  );

  // Before the first publish there is nothing to compare against, so any non-empty
  // draft counts as unsaved. An empty draft that has never been published is not
  // dirty — there is nothing to send.
  // hasAuthorEdits matters in the no-baseline branch: emptying a restored chain leaves nothing to
  // compare and nothing in the list, which read as "clean" and disabled Save — so the author could
  // not publish the clear that would take the standing rooms down. Having edited at all is enough
  // to mean there is something to send.
  // A key-only difference counts, because it counts for UE: a chain re-keyed under the same elements
  // rebuilds and re-stamps every spot id in it, so it is a change the author has to be able to
  // publish — see sameRoomPlacements.
  // Finishes count towards dirtiness on their own. An author who repainted every wall and touched
  // nothing else has changed what everyone will see, and a Save button that stayed dead through it
  // would be telling them there is nothing to publish.
  const finishesChanged = publishedFinishes
    ? !sameFinishSets(finishes, publishedFinishes)
    : finishes.defaults.length > 0 || Object.keys(finishes.rooms).length > 0;

  // The lobby counts on its own, for the same reason a finish does: an author who switched the
  // entrance and touched nothing else has changed the first thing every visitor sees, and a Save
  // button that stayed dead through it would say there was nothing to publish.
  const startChanged =
    publishedStartElement !== null
      ? startElement !== publishedStartElement
      : !!startElement;

  const isDirty = publishedRooms
    ? !sameRoomPlacements(rooms, publishedRooms) ||
      finishesChanged ||
      startChanged
    : hasAuthorEdits || rooms.length > 0 || finishesChanged || startChanged;

  // Declared after the two above because it has to ask them. Revert restores the rooms, the
  // finishes and the lobby together — so gating it on the rooms alone left it dead for the two
  // edits that are not rooms, which is worse than cosmetic: while any draft is up Unreal hides the
  // published layout behind it, and revertDraft is the only thing that clears `hasAuthorEdits` and
  // hands the real rooms back. An author who switched only the entrance was left looking at a
  // local copy of the world with no way to put the real one back.
  const canRevert =
    !!revertTarget &&
    (!sameRoomPlacements(rooms, revertTarget) ||
      finishesChanged ||
      startChanged);

  const value = useMemo<MapEditorContextValue>(
    () => ({
      rooms,
      appendRoom,
      removeRoomAt,
      moveRoom,
      replaceRoomAt,
      setChain,
      clearChain,
      startElement,
      setStartElement,
      revertDraft,
      canRevert,
      publishedRooms,
      markPublished,
      isDirty,
      builtRooms,
      setBuiltRooms,
      refusals,
      setRefusals,
      previewRefusals,
      setPreviewRefusals,
      hydrateFromPersisted,
      hasAuthorEdits,
      finishes,
      setSlotFinish,
      clearSlotFinish,
    }),
    [
      rooms,
      appendRoom,
      removeRoomAt,
      moveRoom,
      replaceRoomAt,
      setChain,
      clearChain,
      startElement,
      setStartElement,
      revertDraft,
      canRevert,
      publishedRooms,
      markPublished,
      isDirty,
      builtRooms,
      setBuiltRooms,
      refusals,
      previewRefusals,
      hydrateFromPersisted,
      hasAuthorEdits,
      finishes,
      setSlotFinish,
      clearSlotFinish,
    ],
  );

  return (
    <MapEditorContext.Provider value={value}>
      {children}
    </MapEditorContext.Provider>
  );
};

/**
 * Reads the map-editor draft.
 *
 * @returns The draft rooms, the edit operations on them, and the two accounts of what is published —
 * the author's own intent and Unreal's report of what is standing.
 */
export const useMapEditor = (): MapEditorContextValue => {
  const ctx = useContext(MapEditorContext);
  if (!ctx) {
    throw new Error('useMapEditor must be used inside MapEditorProvider');
  }
  return ctx;
};

/**
 * Reads the map-editor draft where there may not be one.
 *
 * For the shared chrome, and for that alone. The inspector's header names the selected segment, which
 * means it has to look one up — and the header belongs to every focus in Editor Mode, not to the map
 * editor. Throwing there would make the map editor's provider a requirement for rendering the panel
 * that shows a spot, a hotspot or a script, which is a coupling in the wrong direction.
 *
 * Anything that *edits* the draft uses useMapEditor and throws: a control that silently did nothing
 * outside the provider is worse than one that says where it has to live.
 *
 * @returns The draft, or null when there is no provider above.
 */
export const useMapEditorOrNull = (): MapEditorContextValue | null =>
  useContext(MapEditorContext);
